import { renecPriceDataAtom } from "@/atoms/priceState"
import { connectionAtom } from "@/atoms/settings"
import { IDL } from "@/programs/restaking_program"
import { RESTAKING_PROGRAM_ID } from "@/utils/constants"
import { RestakingAccountRewardType } from "@/utils/dataTypes"
import { AnchorProvider } from "@project-serum/anchor"
import NodeWallet from "@project-serum/anchor/dist/cjs/nodewallet" // using this because we are on browser
import * as anchor from "@project-serum/anchor0.24.2"
import { Keypair, PublicKey } from "@solana/web3.js"
import BN from "bn.js"
import { useAtomValue } from "jotai"
import { useCallback, useMemo } from "react"
import { useQuery } from "react-query"
import useWalletConnect from "./useWalletConnect"

const REWARD_TOKEN_DECIMALS = 9
const CONFIG_SEED = "mywhisk3yneat"
const VAULT_SEED = "mycoff33black"
const STAKING_ACCOUNT_SEED = "assoftasra1n"

type RestakingAccountState = {
  account: {
    publicKey: PublicKey
    owner: PublicKey
    vault: PublicKey
    stakedAmount: BN
    unstakedAmount: BN
    unlockTimestamp: BN
    lastStaked: BN
    rewards: RestakingAccountRewardType[]
  }
}

const opts: anchor.web3.ConfirmOptions = {
  preflightCommitment: "processed",
  commitment: "processed",
}

const RANDOM_KEYPAIR = Keypair.generate()
const RANDOM_WALLET = new NodeWallet(RANDOM_KEYPAIR)
const programIdPubkey = new PublicKey(RESTAKING_PROGRAM_ID)

const useRestakingStats = (enbled: boolean) => {
  const { isRenecChain } = useWalletConnect()
  const connection = useAtomValue(connectionAtom)
  const priceData = useAtomValue(renecPriceDataAtom)
  const provider = useMemo(() => new AnchorProvider(connection, RANDOM_WALLET, opts), [connection])
  const program = useMemo(() => new anchor.Program(IDL, programIdPubkey, provider), [provider])
  const admin = (provider.wallet as anchor.Wallet).payer
  const adminAccount = admin.publicKey

  const relPriceInUSD = priceData.REL ?? 0

  const fetchAllStakingAccountFn = useCallback(async () => {
    const [configAccount] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(CONFIG_SEED), adminAccount.toBuffer()],
      programIdPubkey,
    )

    const [vaultAccount] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(VAULT_SEED), configAccount.toBuffer()],
      programIdPubkey,
    )

    const [,] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(STAKING_ACCOUNT_SEED), vaultAccount.toBuffer(), provider.wallet.publicKey.toBuffer()],
      programIdPubkey,
    )

    const accounts = await program.account.stakingAccount.all()
    return accounts as any as RestakingAccountState[]
  }, [adminAccount, program, provider.wallet.publicKey])

  const fetchStakingAccountRewardsFn = useCallback(async () => {
    if (!isRenecChain) {
      return {
        totalStakingAccounts: 0,
        totalRewardAccumulated: 0,
        totalWaitingToBeUnstaked: 0,
        totalStaked: 0,
        relTVLUsd: 0,
      }
    }

    const accounts = await fetchAllStakingAccountFn()

    let totalRewardAccumulated = 0
    let totalWaitingToBeUnstaked = 0
    let totalStaked = 0

    // Calculating
    accounts.forEach((restakingAccount) => {
      const accumulatedRewardAmount =
        restakingAccount.account.rewards[0].accumulatedRewardAmount.toNumber() / 10 ** REWARD_TOKEN_DECIMALS
      const stakedAmountUI = restakingAccount
        ? restakingAccount.account.stakedAmount.toNumber() / 10 ** REWARD_TOKEN_DECIMALS
        : 0
      const unstakedAmountUI = restakingAccount
        ? restakingAccount.account.unstakedAmount.toNumber() / 10 ** REWARD_TOKEN_DECIMALS
        : 0

      totalRewardAccumulated += accumulatedRewardAmount
      totalWaitingToBeUnstaked += unstakedAmountUI
      totalStaked += stakedAmountUI
    })

    return {
      totalStakingAccounts: accounts.length,
      totalRewardAccumulated,
      totalWaitingToBeUnstaked,
      totalStaked,
      relTVLUsd: relPriceInUSD * totalStaked,
    }
  }, [fetchAllStakingAccountFn, isRenecChain, relPriceInUSD])

  return useQuery(["fetchStakingAccountRewards", relPriceInUSD], fetchStakingAccountRewardsFn, {
    staleTime: 10000,
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.log("fetch stacking error", error)
    },
    enabled: !!enbled,
  })
}

export default useRestakingStats
